import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Closet as ClosetData } from '../lib/queries';
import type { Post } from '../lib/types';
import './Closet.css';

// How long the doors swing on click before we hand off to the post route.
// Matches the door transition in Closet.css plus a short beat. Reduced-motion
// users skip the wait (see open()).
const SWING_MS = 460;

// ---------------------------------------------------------------------------
// Per-closet cabinet art + the door-opening rectangle, keyed by drawer SLUG.
// Every closet is the SAME book-matched walnut wardrobe (one straight-on
// transparent image in public/closets); the only difference is the jewel pair on
// its escutcheons - plate colour + little-stone colour (Kate's eight pairs). The
// two doors are CSS background-slices of that image, split down the middle of the
// rectangle and hinged on its OUTER edges (the true door edges). These cabinets
// carry NO engraving, so the closet name is a DOM plate UNDER the cabinet (it can
// never split). lp/tp/wp/hp are fractions of the image (left, top, width, height).
// ---------------------------------------------------------------------------
type Rect = { lp: number; tp: number; wp: number; hp: number };
interface Art { img: string; ar: number; rect: Rect } // ar = image aspect (w/h)

// The shared wardrobe geometry: art is 1166x1500. The doors hinge on the dark
// REVEAL line that separates each door from the frame (the gold inlay rides on
// the door, just inside that line). Sides x .038 / .962, split down the centre
// gap (.500); top reveal .036, bottom reveal .908.
const W2_AR = 0.777;
const W2_RECT: Rect = { lp: 0.038, tp: 0.036, wp: 0.924, hp: 0.872 };
const cab = (img: string): Art => ({ img, ar: W2_AR, rect: W2_RECT });

const DEFAULT_ART: Art = cab('/closets/cab-pair-amethyst-emerald.webp');

// drawer slug -> jewel-pair cabinet (plate + stone), in wall order.
const CLOSET_ART: Record<string, Art> = {
  'conversion-closeup-1': cab('/closets/cab-pair-amethyst-emerald.webp'), // Open Basket - amethyst + emerald
  'extended-ba':          cab('/closets/cab-pair-sapphire-citrine.webp'), // Conversion Close-up - sapphire + citrine
  'nancy-conversations':  cab('/closets/cab-pair-ruby-peacock.webp'),     // Nancy Noodles - ruby + peacock teal
  'out-and-about':        cab('/closets/cab-pair-turquoise-amber.webp'),  // Out and About - turquoise + burnt amber
  'redesign-challenge':   cab('/closets/cab-pair-fuchsia-cobalt.webp'),   // Redesign Challenge - fuchsia + cobalt
  'conversion-closeup-2': cab('/closets/cab-pair-coral-jade.webp'),       // Mini Class - coral + jade
  'sales-room':           cab('/closets/cab-pair-burgundy-aqua.webp'),    // Saleroom - burgundy + aquamarine
  'monthly-zoom-qa':      cab('/closets/cab-pair-royal-magenta.webp'),    // Zoom Q+A - royal blue + magenta
};

export function artForSlug(slug: string): Art {
  return CLOSET_ART[slug] ?? DEFAULT_ART;
}

// The feed wall order the team designed (by drawer slug). Slugs not listed
// fall to the end of the wall (kept, just after the curated eight).
export const CLOSET_ORDER: string[] = [
  'conversion-closeup-1', 'extended-ba', 'nancy-conversations', 'out-and-about',
  'redesign-challenge', 'conversion-closeup-2', 'sales-room', 'monthly-zoom-qa',
];

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Same cover-resolution rules as PostCard so a post looks identical whether
// it's revealed inside a closet or sitting in the Archive grid.
function coverUrl(post: Post): string | null {
  if (post.thumbnail_url) return post.thumbnail_url;
  return post.media?.thumbnail_url ?? null;
}

// ---------------------------------------------------------------------------
// One cabinet. The frame image is drawn to a canvas with the door opening
// erased; the post cover sits in that opening (z:1); the two door slices swing
// open IN FRONT of the frame (z:3) on hover/focus, and wider on click before
// navigating. Empty closets (no live post) sit shut and are not interactive.
// ---------------------------------------------------------------------------
export function Closet({ drawer, post }: ClosetData) {
  const navigate = useNavigate();
  const sceneRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const interiorRef = useRef<HTMLDivElement>(null);
  const doorsRef = useRef<HTMLDivElement>(null);
  const faceLRef = useRef<HTMLSpanElement>(null);
  const faceRRef = useRef<HTMLSpanElement>(null);
  const [opening, setOpening] = useState(false);

  const art = useMemo(() => artForSlug(drawer.slug), [drawer.slug]);
  const empty = !post;
  // A drawer-level cover beats the front post's media (lets a closet carry its
  // own standalone thumbnail, e.g. the Saleroom).
  const cover = drawer.cover_image_url || (post ? coverUrl(post) : null);

  // Slice engine: redraw on image load and whenever the scene resizes.
  useEffect(() => {
    const scene = sceneRef.current;
    const canvas = canvasRef.current;
    if (!scene || !canvas) return;
    // CSS sizes the box (aspect-ratio + width-fill on mobile / fixed height on
    // desktop); we just read the rendered box and fill it.
    scene.style.aspectRatio = String(art.ar);
    const img = new Image();
    let loaded = false;

    function render() {
      if (!loaded || !scene || !canvas) return;
      const W = scene.offsetWidth;
      const H = scene.offsetHeight;
      if (!W || !H) return;

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      canvas.style.width = `${W}px`;
      canvas.style.height = `${H}px`;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);
      ctx.drawImage(img, 0, 0, W, H);

      const dx = W * art.rect.lp;
      const dy = H * art.rect.tp;
      const dw = W * art.rect.wp;
      const dh = H * art.rect.hp;
      ctx.clearRect(Math.floor(dx), Math.floor(dy), Math.ceil(dw), Math.ceil(dh));

      for (const el of [interiorRef.current, doorsRef.current]) {
        if (!el) continue;
        el.style.left = `${dx}px`;
        el.style.top = `${dy}px`;
        el.style.width = `${dw}px`;
        el.style.height = `${dh}px`;
      }

      const half = dw / 2;
      const size = `${W}px ${H}px`;
      const url = `url("${img.src}")`;
      if (faceLRef.current) {
        faceLRef.current.style.backgroundImage = url;
        faceLRef.current.style.backgroundSize = size;
        faceLRef.current.style.backgroundPosition = `-${dx}px -${dy}px`;
      }
      if (faceRRef.current) {
        faceRRef.current.style.backgroundImage = url;
        faceRRef.current.style.backgroundSize = size;
        faceRRef.current.style.backgroundPosition = `-${dx + half}px -${dy}px`;
      }
    }

    img.onload = () => { loaded = true; render(); };
    img.src = art.img;

    const ro = new ResizeObserver(render);
    ro.observe(scene);
    return () => ro.disconnect();
  }, [art]);

  function open() {
    if (empty || opening) return;
    if (prefersReducedMotion()) {
      navigate(`/p/${post!.slug}`);
      return;
    }
    setOpening(true);
    window.setTimeout(() => navigate(`/p/${post!.slug}`), SWING_MS);
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (empty) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      open();
    }
  }

  // A closet holding this cycle's released content rests popped open (ajar) so
  // it reads as "go inside"; one still awaiting its drop (no post) sits shut and
  // is not interactive. Once content publishes, post != null and it pops open.
  const cls = ['closet', empty ? 'closet--empty' : 'closet--ajar', opening && 'closet--opening']
    .filter(Boolean).join(' ');

  return (
    <figure className={['closet-cell', empty && 'closet-cell--empty'].filter(Boolean).join(' ')}>
      <div
        ref={sceneRef}
        className={cls}
        role={empty ? undefined : 'button'}
        tabIndex={empty ? undefined : 0}
        aria-label={empty
          ? `${drawer.name}, coming soon`
          : `Open ${drawer.name}: ${post!.title}`}
        aria-disabled={empty || undefined}
        onClick={empty ? undefined : open}
        onKeyDown={onKeyDown}
      >
        <canvas ref={canvasRef} className="closet__frame-canvas" aria-hidden="true" />
        <div ref={interiorRef} className="closet__interior" aria-hidden="true">
          {cover && <img className="closet__cover" src={cover} alt="" loading="lazy" />}
        </div>
        <div ref={doorsRef} className="closet__doors" aria-hidden="true">
          <div className="closet__door closet__door--left">
            <span ref={faceLRef} className="closet__face" />
          </div>
          <div className="closet__door closet__door--right">
            <span ref={faceRRef} className="closet__face" />
          </div>
        </div>
      </div>
      {/* Name plate under the cabinet (these wardrobes carry no engraving). */}
      <figcaption className="closet__plate">{drawer.name}</figcaption>
    </figure>
  );
}
